import { useId } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { useTheme } from "@hooks/useTheme";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { fetchVexProgress, fetchVexSnapshot, vexSnapshotQueryKey } from "@apis/vex";
import queryKeys from "@configs/react-query/query-key";
import { getVexProgressBreakdownItems, hasFrozenSnapshotParentContext } from "@constants/vex";

/** Same stage palette as Communication.jsx tags (themeConfig communicationTag*) */
const getBreakdownToneClasses = (colors) => ({
  affected: colors.communicationTagRequest,
  underInvestigation: colors.communicationTagUsage,
  fixed: colors.communicationTagVerification,
  notAffected: colors.communicationTagFulfillment,
});

/**
 * VEX progress chip.
 * - Pass `progress` or processed/total (+ optional status counts) for scoped progress
 * - Or pass scanToolCd + verificationSeq to fetch overall GET /vex/progress
 * - Or pass frozenVexSnapshot to use `progress` from GET /vex/verification-report | merged-sbom | product
 * - showInfoTooltip: blue "i" icon explaining processed count (not the breakdown)
 */
const VexProgressChip = ({
  progress,
  processed,
  total,
  affected,
  notAffected,
  fixed,
  underInvestigation,
  scanToolCd,
  verificationSeq,
  frozenVexSnapshot = null,
  enabled = true,
  className = "",
  labelKey = "status.vex-progress",
  titleKey = "status.vex-progress-title",
  showInfoTooltip = false,
  "data-testid": dataTestId = "vex-progress-chip",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const clientConfig = useClientConfig();
  const tooltipId = useId().replace(/:/g, "");
  const isVexOn = Boolean(clientConfig?.isClientConfigEnabled?.("useVex"));

  const hasExplicitProgress =
    progress != null || (processed != null && total != null);
  const canFetchSnapshot =
    !hasExplicitProgress &&
    Boolean(enabled && isVexOn && hasFrozenSnapshotParentContext(frozenVexSnapshot));
  const canFetchLive =
    !hasExplicitProgress &&
    !canFetchSnapshot &&
    Boolean(enabled && isVexOn && scanToolCd && verificationSeq != null && verificationSeq !== "");

  const snapshotQuery = useQuery({
    queryKey: vexSnapshotQueryKey(frozenVexSnapshot),
    queryFn: () => fetchVexSnapshot(frozenVexSnapshot),
    enabled: canFetchSnapshot,
  });

  const progressQuery = useQuery({
    queryKey: [queryKeys.vexProgress, scanToolCd, verificationSeq],
    queryFn: () => fetchVexProgress({ scanToolCd, verificationSeq }),
    enabled: canFetchLive,
  });

  if (!isVexOn || !enabled) {
    return null;
  }

  const resolvedProgress = hasExplicitProgress
    ? {
        ...(progress || {}),
        processed: progress?.processed ?? processed,
        total: progress?.total ?? total,
        affected: progress?.affected ?? affected,
        notAffected: progress?.notAffected ?? notAffected,
        fixed: progress?.fixed ?? fixed,
        underInvestigation: progress?.underInvestigation ?? underInvestigation,
      }
    : canFetchSnapshot
      ? snapshotQuery.data?.progress
      : progressQuery.data;

  const resolvedProcessed = resolvedProgress?.processed;
  const resolvedTotal = resolvedProgress?.total;

  if (resolvedProcessed == null || resolvedTotal == null || resolvedTotal === 0) {
    return null;
  }

  const infoTooltipText = t(titleKey);
  const label = t(labelKey, {
    processed: resolvedProcessed,
    total: resolvedTotal,
  });
  const breakdownItems = getVexProgressBreakdownItems(resolvedProgress, t);
  const breakdownToneClasses = getBreakdownToneClasses(colors);

  return (
    <span
      className={`inline-flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-md border px-2.5 py-1 ${colors.borderPrimary} ${className}`}
      data-testid={dataTestId}
    >
      <span className="inline-flex items-center gap-1.5">
        <span className={`inline-flex items-center text-xs font-semibold ${colors.textPrimary}`}>{label}</span>
        {showInfoTooltip && (
          <>
            <button
              type="button"
              className={`inline-flex items-center justify-center ${colors.textComparisonLink} ${colors.hoverTextComparisonLink}`}
              data-tooltip-id={`vex-progress-tooltip-${tooltipId}`}
              data-tooltip-content={infoTooltipText}
              aria-label={infoTooltipText}
              data-testid={`${dataTestId}-info`}
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" width={20} height={20} aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <Tooltip
              id={`vex-progress-tooltip-${tooltipId}`}
              place="top"
              className={`!text-xs !rounded-lg !px-3 !py-2 !max-w-[360px] !min-w-[180px] !z-[10050] !break-words ${colors.bgPrimaryDarker} ${colors.textPrimary}`}
              style={{
                backgroundColor: "#f3f4f6",
                color: "#111827",
                fontSize: "12px",
                borderRadius: "8px",
                padding: "8px 12px",
                maxWidth: "360px",
                minWidth: "180px",
                width: "max-content",
                zIndex: 10050,
                wordBreak: "normal",
                whiteSpace: "normal",
                lineHeight: "1.4",
              }}
            />
          </>
        )}
      </span>

      {breakdownItems.length > 0 && (
        <span
          className={`inline-flex flex-wrap items-center gap-1.5 border-l pl-2 ${colors.borderPrimary}`}
          data-testid={`${dataTestId}-breakdown`}
        >
          {breakdownItems.map((item) => (
            <span
              key={item.key}
              className={`inline-flex items-center gap-1 whitespace-nowrap rounded-3xl border px-2 py-0.5 text-xs font-medium ${
                breakdownToneClasses[item.key] || colors.communicationTagDefault
              }`}
              data-testid={`${dataTestId}-breakdown-${item.key}`}
            >
              <span className="opacity-90">{item.label}</span>
              <span className="font-bold tabular-nums">{item.count}</span>
            </span>
          ))}
        </span>
      )}
    </span>
  );
};

export default VexProgressChip;
